import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../utils/image';
import { formatPrice } from '../utils/formatPrice';
import { Link } from 'react-router-dom';

const Cart: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { items, removeFromCart, updateQuantity, total } = useCart();
    const isRTL = i18n.dir() === 'rtl';

    if (items.length === 0) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h2 className="text-2xl font-bold mb-4">{t('common.cartEmpty')}</h2>
                <Link to="/" className="text-primary hover:underline">
                    {t('home.shopNow')}
                </Link>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-neutral-900 mb-8">{t('common.cart')}</h1>

            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden">
                <ul className="divide-y divide-neutral-100">
                    {items.map((item) => (
                        <li key={item.product.id} className="p-6 flex flex-col sm:flex-row items-center gap-6">
                            <img
                                src={getImageUrl(item.product.images[0])}
                                alt={isRTL ? item.product.title_ar : item.product.title_fr}
                                className="w-24 h-24 object-cover rounded-xl"
                            />

                            <div className="flex-grow text-center sm:text-start">
                                <Link to={`/product/${item.product.slug}`} className="text-lg font-bold text-neutral-900 hover:text-primary transition-colors">
                                    {isRTL ? item.product.title_ar : item.product.title_fr}
                                </Link>
                                <p className="text-sm text-neutral-500">
                                    {item.product.categories?.[0]?.name_fr || item.product.category?.name_fr}
                                </p>
                                {item.variant && (
                                    <p className="text-sm text-neutral-500">
                                        {t('product.variant')}: {isRTL ? (item.variant.name_ar || item.variant.name) : item.variant.name}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="flex items-center border rounded-full">
                                    <button
                                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                        className="w-8 h-8 flex items-center justify-center text-neutral-600 hover:bg-neutral-100 rounded-full"
                                    >-</button>
                                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                                    <button
                                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                        className="w-8 h-8 flex items-center justify-center text-neutral-600 hover:bg-neutral-100 rounded-full"
                                    >+</button>
                                </div>

                                <div className="text-lg font-bold text-primary w-24 text-center">
                                    {formatPrice(
                                        (item.product.discount_price || item.product.price) * item.quantity,
                                        item.product.currency,
                                        isRTL ? 'ar-MA' : 'fr-MA'
                                    )}
                                </div>

                                <button
                                    onClick={() => removeFromCart(item.product.id)}
                                    className="text-neutral-400 hover:text-red-500 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>

                <div className="bg-neutral-50 p-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-xl font-bold">
                        {t('common.total')}: <span className="text-primary">{formatPrice(total, 'MAD', isRTL ? 'ar-MA' : 'fr-MA')}</span>
                    </div>
                    <Link
                        to="/checkout"
                        className="px-8 py-3 bg-primary text-white font-bold rounded-full hover:bg-primary/90 transition-colors shadow-lg"
                    >
                        {t('common.checkout')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Cart;
